import { useTranslation } from 'react-i18next';
import { FC, useEffect, useRef, useState } from 'react';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import TextField from '../../core/components/Fields/TextField.tsx';
import FormField from '../../core/components/FormField.tsx';
import { useMutation } from '@apollo/client';
import { DELETE_DOCUMENTS, DISCARD_DOCUMENTS } from '../queries.ts';
import { DocumentType, documentTypeToKey, mapDocumentTypeToGqlDocumentType } from '../constants.ts';
import { useToaster } from '../../core/components/Toast.tsx';
import Button from '../../core/components/Button.tsx';
import Radio from '../../core/components/Radio.tsx';

type ReasonType = 'invalid document type' | 'other_reason';

type DocumentActionModalProps = {
    variant: 'discard' | 'delete';
    visible?: boolean;
    selectedRowKeys: string[];
    documentType: DocumentType;
    initialValue?: string;
    refetch?: () => Promise<unknown>;
    onSuccess: () => void;
    onClose: () => void;
};

const variantConfig = {
    discard: { mutation: DISCARD_DOCUMENTS, keyPrefix: 'discardModal', danger: false },
    delete: { mutation: DELETE_DOCUMENTS, keyPrefix: 'deleteModal', danger: true },
} as const;

const DocumentActionModal: FC<DocumentActionModalProps> = ({
    variant,
    onClose,
    onSuccess,
    refetch,
    initialValue = '',
    documentType,
    selectedRowKeys,
    ...modalProps
}) => {
    const { t } = useTranslation('assistance');
    const { publishToast } = useToaster();

    const { mutation, keyPrefix, danger } = variantConfig[variant];
    const [executeAction, { loading: isSubmitting }] = useMutation(mutation);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [reasonType, setReasonType] = useState<ReasonType>('other_reason');
    const [otherReasonText, setOtherReasonText] = useState(initialValue);

    useEffect(() => {
        if (!modalProps.visible) return;
        setReasonType('other_reason');
        setOtherReasonText(initialValue);
    }, [modalProps.visible]);

    useEffect(() => {
        if (!modalProps.visible || reasonType !== 'other_reason') return;
        textareaRef?.current?.focus();
        setTimeout(() => {
            if (!textareaRef?.current) return;
            const val = textareaRef.current.value;
            textareaRef.current.value = '';
            textareaRef.current.value = val;
        }, 10);
    }, [modalProps.visible, reasonType]);

    const getSubmitReason = () => (reasonType === 'invalid document type' ? reasonType : otherReasonText);

    const onSubmit = async () => {
        try {
            await executeAction({
                variables: {
                    recordIds: selectedRowKeys,
                    documentType: mapDocumentTypeToGqlDocumentType[documentType],
                    reason: getSubmitReason(),
                },
            });

            await refetch?.();
            onSuccess();
            onClose();

            publishToast({
                description: t(`${keyPrefix}.${variant === 'discard' ? 'discardSuccess' : 'deleteSuccess'}`, {
                    count: selectedRowKeys.length,
                }),
                status: 'success',
            });
        } catch (e) {
            console.error(e);
            publishToast({
                description: t(`${keyPrefix}.${variant === 'discard' ? 'discardError' : 'deleteError'}`),
                status: 'error',
            });
        }
    };

    const options: { value: ReasonType; label: string }[] = [
        {
            value: 'invalid document type',
            label: t(`${keyPrefix}.wrongDocumentType.${documentTypeToKey[documentType]}`),
        },
        { value: 'other_reason', label: t(`${keyPrefix}.otherReason`) },
    ];

    return (
        <ConfirmModal
            onConfirm={onSubmit}
            onCancel={onClose}
            danger={danger}
            title={t(`${keyPrefix}.title`, { count: selectedRowKeys.length })}
            buttons={
                <>
                    <Button type="button" onClick={onClose} loading={isSubmitting}>
                        {t(`${keyPrefix}.cancelButton`)}
                    </Button>
                    <Button variant="primary" form="modal-form" onClick={onSubmit} loading={isSubmitting}>
                        {t(`${keyPrefix}.${variant === 'discard' ? 'discardButton' : 'confirmButton'}`, {
                            count: selectedRowKeys.length,
                        })}
                    </Button>
                </>
            }
            {...modalProps}
        >
            <div className="gap-5 flex flex-col">
                {variant === 'discard' ? (
                    <>
                        <span className="text-content-strong font-normal text-sm">
                            {t('discardModal.description_one')}
                        </span>
                        <span className="text-content-strong font-normal text-sm">
                            {t('discardModal.description_two')}
                        </span>
                    </>
                ) : (
                    <>
                        <span className="text-content-strong font-normal text-sm">
                            {t('deleteModal.message', { count: selectedRowKeys.length })}
                        </span>
                        <span className="text-content-strong font-normal text-sm">{t('deleteModal.reasonTitle')}</span>
                    </>
                )}
                <Radio.Group
                    value={reasonType}
                    onValueChange={(value) => setReasonType(value as ReasonType)}
                    className="gap-3"
                >
                    {options.map(({ value, label }) => (
                        <div key={value} className="flex flex-col gap-2">
                            <label className="group flex items-center gap-2 cursor-pointer">
                                <Radio value={value} />
                                <span className="text-content-strong text-sm">{label}</span>
                            </label>
                            {value === 'other_reason' && reasonType === 'other_reason' && (
                                <FormField>
                                    <TextField
                                        ref={textareaRef}
                                        value={otherReasonText}
                                        onValueChange={(v: string) => setOtherReasonText(v)}
                                        placeholder={t(`${keyPrefix}.otherReasonPlaceholder`)}
                                    />
                                </FormField>
                            )}
                        </div>
                    ))}
                </Radio.Group>
            </div>
        </ConfirmModal>
    );
};

export default DocumentActionModal;
